import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardBody, CardHeader, Col, Container, Row, Button, Spinner } from 'reactstrap';
import TableContainer from "../../Components/Common/TableContainerReactTable";
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../index';
import { impersonateUserThunk, stopImpersonationThunk } from '../../slices/auth/impersonate/thunk';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import './styles.css';
import allData from '../../cache/contractors_va.json';
import { Address } from '../../models/Address.interface';
import { Contractor } from '../../models/Contractor.interface'

export interface SortingState {
  id: string;
  desc: boolean;
}

/*
old interface
interface Contractor {
  id: string;
  contractorId: string;
  businessName: string;
  email?: string;
  phone: string;
  categories: string[];
  searches: Array<{
    category: string;
    city: string;
    cage: string;
    state: string;
  }>;
  generalInfo?: string;
  avatar?: string | null;
  address: Address;
  [key: string]: any;
}
*/

interface ContractorExtended extends Contractor{
  [key: string]: any;
}

interface ImpersonateProps {
  onImpersonationChange: () => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  sortBy: SortingState | undefined;
  setSortBy: React.Dispatch<React.SetStateAction<SortingState | undefined>>;
}

const allDataArray: ContractorExtended[] = allData as ContractorExtended[];

const Impersonate: React.FC<ImpersonateProps> = ({ 
  onImpersonationChange, 
  searchTerm, 
  setSearchTerm, 
  sortBy, 
  setSortBy 
}) => {
  const [contractors, setContractors] = useState<ContractorExtended[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const useLocalData = true;

  const handleImpersonate = useCallback(async (contractor: ContractorExtended) => {
    setIsImpersonating(true);
    try {
      /*
      await dispatch(impersonateUserThunk({
        id: contractor.contractorId,
        displayName: contractor.businessName,
        email: contractor.email || 'N/A',
        avatar: contractor.avatar || null,
        phone: contractor.phone,
        address: contractor.address
      }));
      */
      contractor.id = contractor.contractorId;
      contractor.displayName = contractor.businessName;
      await dispatch(impersonateUserThunk(contractor));

      // Use a timeout to ensure state updates are processed
      setTimeout(() => {
        window.dispatchEvent(new Event('storage'));
        onImpersonationChange();
        setIsImpersonating(false);
      }, 100);
      
    } catch (error) {
      console.error('Error during impersonation:', error);
      setIsImpersonating(false);
    }
  }, [dispatch, onImpersonationChange]);

  const handleStopImpersonation = useCallback(async () => {
    try {
      await dispatch(stopImpersonationThunk());
      setTimeout(() => {
        window.dispatchEvent(new Event('storage'));
        onImpersonationChange();
      }, 100);
    } catch (error) {
      console.error('Error stopping impersonation:', error);
    }
  }, [dispatch, onImpersonationChange]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  useEffect(() => {
    const fetchContractors = async () => {
      try {
        setIsLoading(true);
        if (useLocalData) {
          const transformedData = allDataArray
            .filter((item: Contractor) => item.id && item.email)
            .map((item: Contractor) => ({
              ...item,
              id: item.id,
              businessName: item.businessName || '',
              email: item.email,
              phone: item.phone || '',
              categories: item.categories || [],
              searches: item.searches || [],
              generalInfo: item.generalInfo,
              address: item.address,
            }));
          setContractors(transformedData);
        } else {
          const firestore = firebase.firestore();
          const contractorsSnapshot = await firestore.collection('contractors').get();
          const contractorsList = contractorsSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() } as Contractor))
            .filter(contractor => contractor.email && contractor.categories);
          setContractors(contractorsList);
        }
      } catch (error) {
        console.error("Error fetching contractors:", error);
        setContractors([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContractors();
  }, []);

  const filteredData = useMemo(() => {
    return contractors.filter((item: ContractorExtended) => {
      return Object.keys(item).some((key) => {
        const value = item[key];
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        }
        if (Array.isArray(value)) {
          return value.some((val: any) => 
            typeof val === 'string' && val.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        if (typeof value === 'object' && value !== null) {
          return Object.values(value).some((val: any) => 
            typeof val === 'string' && val.toLowerCase().includes(searchTerm.toLowerCase())
          );
        }
        return false;
      });
    });
  }, [contractors, searchTerm]);

  const highlightSearchTerm = (text: string | any) => {
    if (!searchTerm || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) => 
      part.toLowerCase() === searchTerm.toLowerCase() ? 
      <mark key={index} className="searchHighlight">{part}</mark> : part
    );
  };

  const columns = useMemo(
    () => [
      {
        header: 'Business Name',
        accessorKey: 'businessName',
        cell: (info: any) => <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>{highlightSearchTerm(info.getValue())}</div>,
        sortable: true,
      },
      {
        header: 'Email',
        accessorKey: 'email',
        cell: (info: any) => <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>{highlightSearchTerm(info.getValue() || '')}</div>,
        sortable: true,
      },
      {
        header: 'Phone',
        accessorKey: 'phone',
        cell: (info: any) => <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>{highlightSearchTerm(info.getValue())}</div>,
        sortable: true,
      },
      {
        header: 'Categories',
        accessorKey: 'categories',
        cell: (info: any) => <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>{highlightSearchTerm(info.getValue().join(', '))}</div>,
        sortable: true,
      },
      {
        header: 'Searches',
        accessorKey: 'searches',
        cell: (info: any) => {
          const searches = info.getValue();
          return (
            <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>
              {searches.map((search: any, index: number) => (
                <div key={index}>
                  {highlightSearchTerm(`${search.category} - ${search.city}, ${search.state}`)}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: 'General Info',
        accessorKey: 'generalInfo',
        cell: (info: any) => <div style={{ maxWidth: '400px', whiteSpace: 'normal' }}>{highlightSearchTerm(info.getValue() || 'N/A')}</div>,
        sortable: true,
      },
      {
        header: 'Actions',
        accessorKey: 'id',
        cell: (info: any) => (
          <div className="d-flex justify-content-center">
            <Button color="info" onClick={() => handleImpersonate(info.row.original)}>
              Impersonate
            </Button>
          </div>
        ),
      },
    ],
    [searchTerm, handleImpersonate, isImpersonating]
  );


  const sortedData = useMemo(() => {
    if (sortBy) {
      return [...filteredData].sort((a, b) => {
        const aValue = a[sortBy.id];
        const bValue = b[sortBy.id];
        if (aValue < bValue) return sortBy.desc ? 1 : -1;
        if (aValue > bValue) return sortBy.desc ? -1 : 1;
        return 0;
      });
    }
    return filteredData;
  }, [filteredData, sortBy]);

  const handleSort = (column: string) => {
    setSortBy((prev: SortingState | undefined) => {
      if (prev && prev.id === column) {
        return { id: column, desc: !prev.desc };
      }
      return { id: column, desc: false };
    });
  };



  return (
    <React.Fragment>
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Impersonate Contractors</h5>
                <div style={{ width: '700px', position: 'relative' }}>
                  <input
                    type="text"
                    className="form-control pr-5"
                    placeholder="Search contractors..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      color="danger"
                      size="sm"
                      style={{
                        position: 'absolute',
                        right: '5px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                      }}
                      onClick={clearSearch}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                {isLoading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={sortedData}
                    isGlobalFilter={false}
                    customPageSize={10}
                    divClass="table-responsive table-card mb-3"
                    tableClass="table table-nowrap table-centered align-middle table-striped table-hover"
                    theadClass="table-light"
                    SearchPlaceholder="Search contractors..."
                    onSort={handleSort}
                    sortBy={sortBy}
                    showTopPagination={true}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <div className="position-fixed bottom-0 end-0 p-3 stop-impersonation-container">
        <Button color="danger" onClick={handleStopImpersonation}>
          Stop Impersonation
        </Button>
      </div>
    </React.Fragment>
  );
};

export default Impersonate;